import { useEffect, useRef } from "react";
import type { SectionProps } from "../../../components/Questionnaire/Section";
import type { RuleSet } from "../../../components/Questionnaire/validation/form/types";
import { useQuestionnaireActions } from "./useQuestionnaireActions";

/**
 * Initialize questionnaire state without subscribing to changes.
 * Use when a parent already subscribes and you just need to set up state.
 *
 * @example useQuestionnaireInitialization(sections, validations, config);
 * @see useQuestionnaire - initialize + subscribe to state
 */
export const useQuestionnaireInitialization = (
  sections?: SectionProps[],
  validations?: readonly RuleSet[],
  config: Record<string, unknown> = {},
) => {
  const actions = useQuestionnaireActions();
  const isInitialMount = useRef(true);
  const prevConfig = useRef(config);
  const prevSections = useRef(sections);
  const prevValidations = useRef(validations);

  useEffect(() => {
    if (isInitialMount.current) {
      // Initial mount - run all applicable initializations
      if (config && Object.keys(config).length) {
        actions.setConfig(config);
      }
      if (sections) {
        actions.setSections(sections);
      }
      if (validations && Object.keys(validations).length) {
        actions.setValidations(validations);
      }
      isInitialMount.current = false;
    } else {
      // Subsequent updates - only run for changed props
      if (config !== prevConfig.current && Object.keys(config).length) {
        actions.setConfig(config);
      }
      if (sections !== prevSections.current && sections) {
        actions.setSections(sections);
      }
      if (
        validations !== prevValidations.current &&
        validations &&
        Object.keys(validations).length
      ) {
        actions.setValidations(validations);
      }
    }

    prevConfig.current = config;
    prevSections.current = sections;
    prevValidations.current = validations;
  }, [config, sections, validations, actions]);
};
